import { computed, ref, Ref, watch } from "@vue/composition-api";
import { gen_id } from "@/fp";
export interface DropDownItem {
  id: string;
  name: string;
  icon?: string;
}

export interface MultiSelectionDropDownItem {
  id: string;
  name: string;
  selected: boolean;
  icon?: string;
}

export const UseDropdownItems = <T extends DropDownItem>(
  seed: T[] = [],
  on_change?: () => void
): [
  Ref<T[]>,
  Readonly<Ref<Readonly<T | undefined>>>,
  (id: string | { id: string }) => void,
  (new_seed: T[]) => T[],
  (fn: (item_id: string) => void) => () => void,
  () => void
] => {
  const items: Ref<T[]> = ref(seed);
  const _selected_item_id = ref("");
  const selected_item = computed(() => {
    if (_selected_item_id.value) {
      return items.value.find((l) => l.id === _selected_item_id.value);
    } else {
      return undefined;
    }
  });
  const set_selected_item = (id: string | { id: string }) => {
    const val_before = _selected_item_id.value;
    if (typeof id === "string") {
      _selected_item_id.value = id;
    } else {
      _selected_item_id.value = id.id;
    }
    if (val_before != _selected_item_id.value && on_change) {
      on_change();
    }
    notify_watchers(_selected_item_id.value);
  };

  const notify_watchers = (new_selected_id: string) => {
    Object.values(watchers).forEach((w) => w(new_selected_id));
  };

  if (seed.length > 0) {
    _selected_item_id.value = seed[0].id;
  }

  const init_seed = (new_seed: T[]) => (items.value = new_seed);

  const gen_watcher_id = gen_id("w_");
  const watchers: Record<string, (item_id: string) => void> = {};

  const add_watcher = (fn: (item_id: string) => void) => {
    const watcher_id = gen_watcher_id();
    watchers[watcher_id] = fn;
    return () => {
      delete watchers[watcher_id];
    };
  };

  const cleanup = () => {
    // remove_watcher && remove_watcher();
  };

  return [
    items,
    selected_item,
    set_selected_item,
    init_seed,
    add_watcher,
    cleanup,
  ];
};

export const UseMultiSelectionDropdownItems = <
  T extends MultiSelectionDropDownItem
>(
  seed: T[]
): [
  Ref<T[]>,
  Readonly<Ref<readonly T[]>>,
  (id: string) => void,
  (new_seed: T[]) => T[],
  (fn: (items: T[]) => void) => () => void,
  () => void
] => {
  const items: Ref<T[]> = ref(seed);

  const selected_items = computed(() => items.value.filter((f) => f.selected));
  const set_selected_item = (id: string | { id: string }) => {
    items.value = items.value.map((d) => {
      if (d.id === id) {
        d.selected = !d.selected;
      }
      return d;
    });
  };

  const init_seed = (new_seed: T[]) => (items.value = new_seed);

  const gen_watcher_id = gen_id("w_");
  const watchers: Record<string, (items: T[]) => void> = {};
  let remove_watcher = () => {
    /*  */
  };
  const add_watcher = (fn: (items: T[]) => void) => {
    const watcher_id = gen_watcher_id();
    watchers[watcher_id] = fn;
    if (Object.keys(watchers).length === 0) {
      remove_watcher = watch(selected_items, (new_items) => {
        const res = [...new_items];
        Object.values(watchers).forEach((w) => w(res));
      });
    }
    return () => {
      delete watchers[watcher_id];
    };
  };

  const cleanup = () => {
    remove_watcher && remove_watcher();
  };
  return [
    items,
    selected_items,
    set_selected_item,
    init_seed,
    add_watcher,
    cleanup,
  ];
};
